// AGC: Common formatting utilities for farming data

/**
 * AGC: Format area in hectares with appropriate precision
 * @param hectares - Area in hectares
 * @returns Formatted string with unit
 */
export const formatArea = (hectares: number | undefined | null): string => {
  // AGC: Handle undefined/null values gracefully
  if (hectares === undefined || hectares === null || isNaN(hectares)) {
    return '-- ha'
  }
  if (hectares < 0.01) {
    return `${(hectares * 10000).toFixed(0)} m²`
  }
  if (hectares < 1) {
    return `${hectares.toFixed(2)} ha`
  }
  return `${hectares.toFixed(1)} ha`
}

/**
 * AGC: Format weight/yield with appropriate unit
 * @param kg - Weight in kilograms
 * @returns Formatted string with unit
 */
export const formatWeight = (kg: number): string => {
  if (kg < 1) {
    return `${(kg * 1000).toFixed(0)} g`
  }
  if (kg < 1000) {
    return `${kg.toFixed(1)} kg`
  }
  return `${(kg / 1000).toFixed(2)} t`
}

/**
 * AGC: Format currency in Brazilian Real (BRL)
 * @param amount - Amount in currency
 * @param currency - Currency code (default: BRL)
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number, 
  currency: string = 'BRL'
): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(amount)
}

/**
 * AGC: Format date for display in Brazilian format
 * @param date - Date object or string
 * @returns Formatted date string
 */
export const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('pt-BR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d)
}

/**
 * AGC: Format date with time
 * @param date - Date object or string
 * @returns Formatted date and time string
 */
export const formatDateTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('pt-BR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

/**
 * AGC: Format relative time (e.g., "3 dias atrás")
 * @param date - Date object or string
 * @returns Relative time string in Portuguese
 */
export const formatRelativeTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  
  if (diffMinutes < 1) return 'Agora'
  if (diffMinutes < 60) return `${diffMinutes} min atrás`
  if (diffHours < 24) return `${diffHours}h atrás`
  if (diffDays === 0) return 'Hoje'
  if (diffDays === 1) return 'Ontem'
  if (diffDays < 7) return `${diffDays} dias atrás`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} semanas atrás`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} meses atrás`
  return `${Math.floor(diffDays / 365)} anos atrás`
}

/**
 * AGC: Format temperature with unit
 * @param celsius - Temperature in Celsius
 * @returns Formatted temperature string
 */
export const formatTemperature = (celsius: number): string => {
  return `${Math.round(celsius)}°C`
}

/**
 * AGC: Format percentage
 * @param value - Percentage value (0-100)
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number): string => {
  return `${Math.round(value)}%`
}

/**
 * AGC: Format wind speed
 * @param kmh - Speed in km/h
 * @returns Formatted speed string
 */
export const formatWindSpeed = (kmh: number): string => {
  return `${Math.round(kmh)} km/h`
}

/**
 * AGC: Format number with thousands separator
 * @param value - Number to format
 * @returns Formatted number string
 */
export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('pt-BR').format(value)
}







